// greetings.js — ONE source of truth for the "this birthday greeting is
// already handled" signal.
//
// Four screens read and write that signal: Inicio → "Saludos y Felicitaciones",
// Inicio → "Alertas pendientes", the Alertas module, the login popup, and
// Eventos del mes. Until this module they each built the TeamAction key their
// own way, and the disagreement is exactly what the 2026-08-14 ticket
// ("Cumpleaños", Maricarmen Nieto / Victor Romero) describes: greetings sent
// from Alertas kept showing red on Inicio, they were re-processed "para que
// quedaran en verde", and the same clients received several copies at
// different hours.
//
// Two disagreements caused it:
//
//   1. WHO the greeting is for. "Saludos y Felicitaciones" dedups per PERSON
//      (`<clientId>|spouse`, `<clientId>|dep3`) because one household can have
//      several birthdays; every other screen wrote the bare `<clientId>`. So a
//      spouse greeting sent from Alertas never marked the Inicio card — and a
//      primary greeting sent from Inicio never silenced the Alertas row.
//
//   2. WHICH DAY the marker belongs to. The key was stamped with the UTC day
//      the button was pressed. For a Florida agency that is already TOMORROW
//      after 8 PM ET (every other birthday helper here is local-day), and for
//      a greeting SCHEDULED for a future birthday it is the wrong day
//      outright: the marker expired at midnight, so the birthday came back as
//      pending on the very morning the queued email was due to go out.
//
// The rule now: a birthday marker is keyed by (birthday PERSON, LOCAL date of
// the birthday occurrence being greeted) — never by the day the button was
// pressed. Scheduling tomorrow's greeting today stamps TOMORROW, so tomorrow's
// screens still read "✅ Enviado" instead of inviting a second send.
//
// Note the primary-client key is still the bare clientId, so every marker
// written before this module keeps resolving for the common case.

// Wall-clock date in the user's LOCAL timezone as YYYY-MM-DD. Never
// toISOString() — that is UTC and files a 9 PM ET action under tomorrow.
export const localDayKey = (d = new Date()) => {
  const x = d instanceof Date ? d : new Date(d);
  if (isNaN(x.getTime())) return null;
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

// Mirrors parseDob in deskte-full.jsx / DashboardView.jsx: accepts YYYY-MM-DD
// and M/D/YYYY, always as a LOCAL date (appending T00:00:00 keeps the
// YYYY-MM-DD form from being read as UTC midnight).
export const parseDobLocal = (dob) => {
  if (!dob) return new Date(NaN);
  const s = String(dob).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + 'T00:00:00');
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return new Date(+m[3], +m[1] - 1, +m[2]);
  return new Date(s);
};

// Canonical identity of the person being greeted. Matches the `_bkey` shape
// "Saludos y Felicitaciones" already used, so existing markers keep resolving.
//   primary    → "c1"
//   spouse     → "c1|spouse"
//   dependent  → "c1|dep7"   (dependent id, or its index when it has none)
export const bdayPersonKey = (clientId, rel, idx) => {
  if (!clientId) return '';
  if (rel === 'spouse') return `${clientId}|spouse`;
  if (rel === 'dep') return `${clientId}|dep${idx == null ? '' : idx}`;
  return String(clientId);
};

// A quién saluda una fila de la cola, en la forma que el servidor sabe leer
// para rearmarla al enviar (deskte-api/src/utils/envioProgramadoAlDia.ts):
// 'titular' | 'spouse' | 'dep:<id>'. El id de un dependiente antiguo sin id es
// `dep_<posición>`, el mismo que le pone crm-app/api.js al leer la ficha.
export const personaDelSaludo = (personKey) => {
  const k = String(personKey || '');
  const i = k.indexOf('|');
  if (i < 0) return 'titular';
  const rest = k.slice(i + 1);
  if (rest === 'spouse') return 'spouse';
  if (rest.startsWith('dep')) {
    const id = rest.slice(3);
    return 'dep:' + (/^\d+$/.test(id) ? 'dep_' + id : id);
  }
  return 'titular';
};

// Lo que va en `createScheduledSend` para que el correo salga con la plantilla
// y la ficha del DÍA DEL ENVÍO, no con la copia de hoy (Lissette 2026-10-14).
// Solo cuando el texto es exactamente el de la plantilla: uno editado a mano
// se manda tal cual. `{}` = nada que añadir.
export const plantillaVivaDelSaludo = ({ sinTocar, templateId, tipo, lang, personKey }) =>
  (sinTocar && templateId && (lang === 'es' || lang === 'en'))
    ? { templateId, plantillaViva: { tipo, lang, persona: personaDelSaludo(personKey) } }
    : {};

// LOCAL YYYY-MM-DD of the birthday occurrence a greeting sent NOW would be
// for: this year's if it hasn't passed, next year's otherwise. This is the
// date component of the marker — a greeting for a birthday three days out
// belongs to that day, not to today.
export const birthdayDayKey = (dob, from = new Date()) => {
  const b = parseDobLocal(dob);
  if (isNaN(b.getTime())) return null;
  const today = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let next = new Date(today.getFullYear(), b.getMonth(), b.getDate());
  if (next < today) next = new Date(today.getFullYear() + 1, b.getMonth(), b.getDate());
  return localDayKey(next);
};

// The TeamAction key itself. Kept in one place so no caller re-derives it.
export const bdayGreetingKey = (personKey, dayKey) =>
  `bday_${personKey}_${dayKey || localDayKey()}`;

// Default moment for a greeting whose birthday is still ahead: 9:00 AM local
// on the birthday, in the 'YYYY-MM-DDTHH:mm' shape DateTimeField and
// <input type="datetime-local"> expect. Returns '' for a birthday that is
// TODAY — those are meant to go out now, and pre-filling a past 9 AM would
// only get rejected by the backend's "runAt is in the past" guard.
export const defaultGreetingScheduleAt = (dayKey, hour = 9) => {
  if (!dayKey || dayKey <= localDayKey()) return '';
  return `${dayKey}T${String(hour).padStart(2, '0')}:00`;
};

/**
 * "Esta persona no tiene correo propio: esto sale al correo del titular."
 *
 * Cuando el cumpleaños es de un cónyuge o un dependiente SIN correo propio, el
 * saludo se manda a la dirección del titular — dirigido por nombre a quien
 * cumple. Es lo razonable: mejor que llegue a la casa a que no llegue.
 *
 * Pero las pantallas solo enseñaban la dirección entre `< >` junto al nombre de
 * quien cumple, sin decir de quién es. El dato estaba y aun así se leía mal:
 * "María <juan@correo.com>" parece un correo de María si vas con prisa
 * (Lissette 2026-08-17: "¿qué pasa cuando no tenga correo? ¿se le envía al
 * titular?" — la pregunta la hizo alguien que conoce el sistema, así que la
 * pantalla no lo estaba diciendo).
 *
 * Un texto, un solo sitio, y las tres pantallas dicen exactamente lo mismo —
 * misma razón por la que la llave del saludo vive aquí y no en cuatro copias.
 *
 * Devuelve null cuando no hay nada que advertir: la persona tiene su propio
 * correo, es el titular, o no hay dirección ninguna (ahí el envío se bloquea y
 * el aviso sobraría).
 */
export const borrowedEmailNotice = ({ ownEmail, deliverTo, personName, ownerName, rel, lang = 'es' } = {}) => {
  if (!deliverTo || ownEmail || !rel) return null;
  const es = lang !== 'en';
  const quien = (personName || '').trim() || (es ? 'Esta persona' : 'This person');
  const duenno = (ownerName || '').trim();
  return es
    ? `${quien} no tiene correo propio en la ficha, así que este saludo sale a ${deliverTo}${duenno ? ` — el correo de ${duenno}` : ''}. El mensaje va dirigido a ${quien} por su nombre.`
    : `${quien} has no email of their own on file, so this greeting goes to ${deliverTo}${duenno ? ` — ${duenno}'s address` : ''}. The message is still addressed to ${quien} by name.`;
};
